import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  TruckIcon, 
  ChartBarIcon, 
  ClockIcon,
  PlusCircleIcon,
  MapPinIcon,
  UserGroupIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  BellIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import { obtenerColorClase } from '../../../utils/tema/colores'

// Interfaces para tipado
interface EstadisticaRapida {
  id: string
  titulo: string
  valor: string | number
  icono: React.ComponentType<any>
  color: string
  cambio?: string
  tendencia?: 'up' | 'down' | 'neutral'
}

interface SeccionPrincipal {
  id: string
  titulo: string
  descripcion: string
  icono: React.ComponentType<any>
  color: string
  gradiente: string
  acciones: {
    titulo: string
    descripcion: string
    ruta: string
    icono: React.ComponentType<any>
  }[]
}

interface ActividadReciente {
  id: string
  tipo: 'envio_creado' | 'entrega_completada' | 'vehiculo_mantenimiento' | 'conductor_asignado'
  titulo: string
  descripcion: string
  tiempo: string
  icono: React.ComponentType<any>
  color: string
}

const PaginaDashboardUsuario: React.FC = () => {
  const { usuario } = useAuth()
  const [estadisticas, setEstadisticas] = useState<EstadisticaRapida[]>([])
  const [actividades, setActividades] = useState<ActividadReciente[]>([])
  const [cargando, setCargando] = useState(true)



  // Datos de las secciones principales
  const seccionesPrincipales: SeccionPrincipal[] = [
    {
      id: 'envios',
      titulo: 'Gestión de Envíos',
      descripcion: 'Administra todos los aspectos de tus envíos desde la creación hasta la entrega final',
      icono: TruckIcon,
      color: 'blue',
      gradiente: 'from-blue-500 to-blue-600',
      acciones: [
        {
          titulo: 'Crear Envío',
          descripcion: 'Registra un nuevo envío',
          ruta: '/envios/crear',
          icono: PlusCircleIcon
        },
        {
          titulo: 'Ver Envíos',
          descripcion: 'Lista de todos los envíos',
          ruta: '/envios',
          icono: EyeIcon
        },
        {
          titulo: 'Seguimiento',
          descripcion: 'Rastrea tus envíos en tiempo real',
          ruta: '/envios/seguimiento',
          icono: MapPinIcon
        }
      ]
    },
    {
      id: 'flota',
      titulo: 'Flotas y Vehículos',
      descripcion: 'Gestiona tu flota de vehículos y optimiza las operaciones logísticas',
      icono: TruckIcon,
      color: 'green',
      gradiente: 'from-green-500 to-green-600',
      acciones: [
        {
          titulo: 'Gestión de Flota',
          descripcion: 'Administra tus vehículos',
          ruta: '/flota',
          icono: TruckIcon
        },
        {
          titulo: 'Conductores',
          descripcion: 'Gestiona tu equipo de conductores',
          ruta: '/conductores',
          icono: UserGroupIcon
        },
        {
          titulo: 'Mantenimiento',
          descripcion: 'Programa y rastrea mantenimientos',
          ruta: '/mantenimiento',
          icono: CogIcon
        }
      ]
    },
    {
      id: 'reportes',
      titulo: 'Reportes y Análisis',
      descripcion: 'Analiza el rendimiento de tu operación logística con reportes detallados',
      icono: ChartBarIcon,
      color: 'purple',
      gradiente: 'from-purple-500 to-purple-600',
      acciones: [
        {
          titulo: 'Rendimiento',
          descripcion: 'Métricas de operación',
          ruta: '/reportes/rendimiento',
          icono: ArrowTrendingUpIcon
        },
        {
          titulo: 'Financieros',
          descripcion: 'Análisis de costos e ingresos',
          ruta: '/reportes/financieros',
          icono: ChartBarIcon
        },
        {
          titulo: 'Estadísticas',
          descripcion: 'Datos históricos y tendencias',
          ruta: '/reportes/estadisticas',
          icono: ChartBarIcon
        }
      ]
    }
  ]

  // Cargar datos del dashboard
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        // Simular carga de datos (reemplazar con llamadas reales a la API)
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        setEstadisticas([
          {
            id: 'envios-activos',
            titulo: 'Envíos Activos',
            valor: Math.floor(Math.random() * 50) + 10,
            icono: TruckIcon,
            color: 'blue',
            cambio: '+12%',
            tendencia: 'up'
          },
          {
            id: 'vehiculos-disponibles',
            titulo: 'Vehículos Disponibles',
            valor: Math.floor(Math.random() * 20) + 5,
            icono: TruckIcon,
            color: 'green',
            cambio: '+5%',
            tendencia: 'up'
          },
          {
            id: 'entregas-hoy',
            titulo: 'Entregas Hoy',
            valor: Math.floor(Math.random() * 15) + 3,
            icono: CalendarDaysIcon,
            color: 'orange',
            cambio: '-2%',
            tendencia: 'down'
          },
          {
            id: 'eficiencia',
            titulo: 'Eficiencia',
            valor: `${(Math.random() * 20 + 80).toFixed(1)}%`,
            icono: ArrowTrendingUpIcon,
            color: 'purple',
            cambio: '+3%',
            tendencia: 'up'
          }
        ])

        setActividades([
          {
            id: '1',
            tipo: 'envio_creado',
            titulo: 'Nuevo envío creado',
            descripcion: 'Envío #12345 - Destino: Ciudad de México',
            tiempo: 'Hace 2 horas',
            icono: PlusCircleIcon,
            color: 'blue'
          },
          {
            id: '2',
            tipo: 'entrega_completada',
            titulo: 'Entrega completada',
            descripcion: 'Envío #12340 - Cliente: Empresa ABC',
            tiempo: 'Hace 4 horas',
            icono: CheckCircleIcon,
            color: 'green'
          },
          {
            id: '3',
            tipo: 'vehiculo_mantenimiento',
            titulo: 'Vehículo en mantenimiento',
            descripcion: 'Camión #VH-001 - Mantenimiento programado',
            tiempo: 'Hace 6 horas',
            icono: ExclamationTriangleIcon,
            color: 'orange'
          }
        ])

        setCargando(false)
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error)
        setCargando(false)
      }
    }

    cargarDatos()
    
    // Configurar actualizaciones automáticas cada 30 segundos
    const intervalo = setInterval(cargarDatos, 30000)
    return () => clearInterval(intervalo)
  }, [])

  // obtenerColorClase ahora proviene del util compartido

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Cargando Dashboard...</h2>
          <p className="text-gray-600 dark:text-gray-300">Preparando tu panel de control</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Encabezado de bienvenida */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl p-8 mb-8 shadow-xl overflow-hidden relative"
        >
          {/* Elementos decorativos */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white/10"></div>
            <div className="absolute left-1/4 -bottom-16 w-64 h-64 rounded-full bg-white/10"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="mb-6 md:mb-0">
                <motion.h1 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl md:text-5xl font-bold mb-4"
                >
                  Dashboard
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl text-purple-100"
                >
                  Tu panel de control para gestión logística
                </motion.p>
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 flex items-center space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-5 w-5" />
                    <span className="text-purple-100">{usuario?.empresa || 'Tu Empresa'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5" />
                    <span className="text-purple-100">
                      {new Date().toLocaleDateString('es-ES', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </motion.div>
              </div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 }}
                className="hidden lg:block"
              >
                <TruckIcon className="h-24 w-24 opacity-30" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Estadísticas Principales Mejoradas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-purple-500 mr-3" />
            Estadísticas en Tiempo Real
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {estadisticas.map((stat, index) => (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${
                    stat.color === 'blue' ? 'from-blue-500 to-blue-600' :
                    stat.color === 'green' ? 'from-green-500 to-green-600' :
                    stat.color === 'orange' ? 'from-orange-500 to-orange-600' :
                    stat.color === 'purple' ? 'from-purple-500 to-purple-600' :
                    'from-gray-500 to-gray-600'
                  } shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <stat.icono className="h-6 w-6 text-white" />
                  </div>
                  {stat.cambio && (
                    <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      stat.tendencia === 'up' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      stat.tendencia === 'down' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                      <span className="mr-1">
                        {stat.tendencia === 'up' ? '↗️' : stat.tendencia === 'down' ? '↘️' : '→'}
                      </span>
                      {stat.cambio}
                    </div>
                  )}
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {stat.titulo}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                    {stat.valor}
                  </p>
                </div>
                
                {/* Barra de progreso decorativa */}
                <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.random() * 100}%` }}
                    transition={{ delay: 1 + index * 0.2, duration: 1 }}
                    className={`h-1 rounded-full bg-gradient-to-r ${
                      stat.color === 'blue' ? 'from-blue-500 to-blue-600' :
                      stat.color === 'green' ? 'from-green-500 to-green-600' :
                      stat.color === 'orange' ? 'from-orange-500 to-orange-600' :
                      stat.color === 'purple' ? 'from-purple-500 to-purple-600' :
                      'from-gray-500 to-gray-600'
                    }`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>




        {/* Secciones principales del sistema mejoradas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 flex items-center">
            <TruckIcon className="h-6 w-6 text-green-500 mr-3" />
            Gestión Logística
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {seccionesPrincipales.map((seccion, index) => (
              <motion.div
                key={seccion.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ y: -8, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl dark:shadow-gray-900/20 transition-all duration-300 border border-gray-100 dark:border-gray-700"
              >
                {/* Fondo decorativo con gradiente */}
                <div className={`absolute inset-0 bg-gradient-to-br ${seccion.gradiente} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
                
                <div className={`bg-gradient-to-r ${seccion.gradiente} p-6 text-white relative`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{seccion.titulo}</h3>
                      <p className="text-white/80 text-sm">{seccion.descripcion}</p>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <seccion.icono className="h-8 w-8 opacity-80" />
                    </motion.div>
                  </div>
                  
                  {/* Elementos decorativos */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10" />
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8" />
                </div>
                
                <div className="p-6 relative">
                  <div className="space-y-3">
                    {seccion.acciones.map((accion, accionIndex) => (
                      <motion.a
                        key={accion.titulo}
                        href={accion.ruta}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + accionIndex * 0.1 }}
                        whileHover={{ x: 8, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group/action border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${seccion.gradiente} bg-opacity-10 group-hover/action:bg-opacity-20 transition-all duration-200`}>
                            <accion.icono className={`h-5 w-5 ${obtenerColorClase(seccion.color as any, 'text')}`} />
                          </div>
                          <div>
                            <span className="font-semibold text-gray-800 dark:text-gray-200 group-hover/action:text-blue-600 dark:group-hover/action:text-blue-400 transition-colors duration-200">
                              {accion.titulo}
                            </span>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{accion.descripcion}</p>
                          </div>
                        </div>
                        <motion.div
                          initial={{ x: 0 }}
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.2 }}
                          className="bg-gray-100 dark:bg-gray-700 group-hover/action:bg-blue-100 dark:group-hover/action:bg-blue-900/30 rounded-full p-2 transition-colors duration-200"
                        >
                          <ChevronRightIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover/action:text-blue-600 dark:group-hover/action:text-blue-400 transition-colors duration-200" />
                        </motion.div>
                      </motion.a>
                    ))}
                  </div>
                  
                  {/* Contador de acciones */}
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{seccion.acciones.length} acciones disponibles</span>
                      <div className="flex space-x-1">
                        {seccion.acciones.map((_, i) => (
                          <div key={i} className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${seccion.gradiente}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Efecto de brillo en hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Actividad reciente mejorada */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/20 p-6 border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Actividad Reciente
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Últimas actualizaciones del sistema
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-all duration-200 border border-blue-200 dark:border-blue-800"
            >
              <BellIcon className="h-4 w-4" />
              <span className="text-sm font-medium">Ver todas</span>
            </motion.button>
          </div>
          
          <div className="space-y-3">
            <AnimatePresence>
              {actividades.map((actividad, index) => (
                <motion.div
                  key={actividad.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ x: 8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-300 cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                >
                  {/* Línea de tiempo */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-gray-300 to-gray-100 dark:from-gray-600 dark:to-gray-700 rounded-full" />
                  
                  <div className={`relative p-3 rounded-xl bg-gradient-to-r ${
                    actividad.color === 'blue' ? 'from-blue-500 to-blue-600' :
                    actividad.color === 'green' ? 'from-green-500 to-green-600' :
                    actividad.color === 'orange' ? 'from-orange-500 to-orange-600' :
                    actividad.color === 'purple' ? 'from-purple-500 to-purple-600' :
                    'from-gray-500 to-gray-600'
                  } shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <actividad.icono className="h-5 w-5 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                          {actividad.titulo}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                          {actividad.descripcion}
                        </p>
                      </div>
                      <div className="flex flex-col items-end ml-4">
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          {actividad.tiempo}
                        </span>
                        <div className="mt-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicador de nueva actividad */}
                  {index < 2 && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    </div>
                  )}
                  
                  {/* Efecto de hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300" />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          {/* Pie de sección */}
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>Última actualización: hace 2 minutos</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Sistema activo</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default PaginaDashboardUsuario
